import React, { useCallback, useEffect, useMemo, useState } from "react";
import { deleteQuestion, fetchAnswerKey, fetchAllData } from "./quiz3Utils";
import Quiz3QuestionForm from "./Quiz3QuestionForm";

const difficultyLabel = { easy: "Dễ", medium: "Trung bình", hard: "Khó" };

// QA-04 (danh sách/ngân hàng câu hỏi) + QA-05 (sửa/xóa — chỉ người tạo).
const Quiz3QuestionBank = ({ uid, subjectId, topicId }) => {
  const [subjects, setSubjects] = useState({});
  const [topics, setTopics] = useState({});
  const [questions, setQuestions] = useState({});
  const [keyword, setKeyword] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // correctOptionId nằm ở node "answerKeys" riêng, không có sẵn trong bulk
  // fetch "questions" nữa — phải tải riêng đúng 1 câu khi mở form sửa.
  const [editingCorrectOptionId, setEditingCorrectOptionId] = useState(undefined);

  useEffect(() => {
    if (!editingId) {
      setEditingCorrectOptionId(undefined);
      return;
    }
    let cancelled = false;
    fetchAnswerKey(editingId).then((correctOptionId) => {
      if (!cancelled) setEditingCorrectOptionId(correctOptionId || "a");
    });
    return () => {
      cancelled = true;
    };
  }, [editingId]);

  const reload = useCallback(async () => {
    const data = await fetchAllData();
    setSubjects(data.subjects || {});
    setTopics(data.topics || {});
    setQuestions(data.questions || {});
  }, []);

  useEffect(() => {
    reload().catch(() => {});
  }, [reload]);

  const list = useMemo(() => {
    return Object.entries(questions)
      .map(([id, q]) => ({ id, ...q }))
      .filter((q) => (subjectId ? q.subjectId === subjectId : true))
      .filter((q) => (topicId ? q.topicId === topicId : true))
      .filter((q) => (difficultyFilter === "all" ? true : q.difficulty === difficultyFilter))
      .filter((q) => (keyword.trim() ? q.content.toLowerCase().includes(keyword.trim().toLowerCase()) : true));
  }, [questions, subjectId, topicId, difficultyFilter, keyword]);

  const handleDelete = async (question) => {
    if (question.createdByUid !== uid) return;
    if (!window.confirm("Xóa câu hỏi này? Không thể hoàn tác.")) return;
    await deleteQuestion(question.id);
    await reload();
  };

  if (creating || editingId) {
    if (editingId && editingCorrectOptionId === undefined) {
      return <p style={{ color: "#6b7280" }}>Đang tải câu hỏi...</p>;
    }
    const existing = editingId ? { id: editingId, ...questions[editingId], correctOptionId: editingCorrectOptionId } : null;
    return (
      <Quiz3QuestionForm
        uid={uid}
        subjectId={existing ? existing.subjectId : subjectId}
        topicId={existing ? existing.topicId : topicId}
        existing={existing}
        onDone={() => {
          setCreating(false);
          setEditingId(null);
          reload().catch(() => {});
        }}
        onCancel={() => {
          setCreating(false);
          setEditingId(null);
        }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Tìm theo từ khóa..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14 }}
        />
        <select
          value={difficultyFilter}
          onChange={(e) => setDifficultyFilter(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14 }}
        >
          <option value="all">Tất cả độ khó</option>
          <option value="easy">Dễ</option>
          <option value="medium">Trung bình</option>
          <option value="hard">Khó</option>
        </select>
        <button
          onClick={() => setCreating(true)}
          disabled={!subjectId}
          title={!subjectId ? "Chọn 1 môn học/chủ đề ở cây bên trái trước" : ""}
          style={primaryBtnStyle}
        >
          + Tạo câu hỏi mới
        </button>
      </div>

      {!subjectId && <p style={{ color: "#6b7280" }}>Chọn 1 môn học/chủ đề ở cây bên trái để xem/tạo câu hỏi.</p>}

      {subjectId && list.length === 0 && <p style={{ color: "#6b7280" }}>Chưa có câu hỏi nào ở đây.</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((q) => (
          <div key={q.id} style={questionRowStyle}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>{q.content}</p>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                {subjects[q.subjectId]?.name}
                {q.topicId && topics[q.topicId] ? ` › ${topics[q.topicId].name}` : ""} · {difficultyLabel[q.difficulty] || q.difficulty}
                {q.fixedQuizId ? " · đã thuộc 1 đề cố định" : ""}
              </p>
            </div>
            {q.createdByUid === uid ? (
              <div style={{ display: "flex", gap: 6 }}>
                <button style={secondaryBtnStyle} onClick={() => setEditingId(q.id)}>
                  Sửa
                </button>
                <button style={dangerBtnStyle} onClick={() => handleDelete(q)}>
                  Xóa
                </button>
              </div>
            ) : (
              <span style={{ fontSize: 12, color: "#9ca3af" }}>Của người khác</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const questionRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 12,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  background: "#fff",
};

const primaryBtnStyle = {
  padding: "6px 14px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

const secondaryBtnStyle = {
  padding: "6px 12px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 13,
};

const dangerBtnStyle = {
  ...secondaryBtnStyle,
  color: "#dc2626",
  borderColor: "#fecaca",
};

export default Quiz3QuestionBank;
